import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Plus, 
  Search, 
  Filter,
  Download,
  Share2,
  Edit,
  Trash2,
  Eye,
  Calendar,
  User,
  Tag,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface MedicalRecord {
  id: string;
  date: string;
  type: string;
  provider_id?: string;
  diagnosis?: string;
  treatment?: string;
  medications?: string[];
  test_results?: any;
  notes?: string;
  attachments?: string[];
  created_at: string;
}

export function MobileMedicalRecords() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [records, setRecords] = useState<MedicalRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');

  // Load medical records
  useEffect(() => {
    const loadRecords = async () => {
      if (!user) return;

      try {
        setLoading(true);
        setError(null);

        const { data, error } = await supabase
          .from('medical_records')
          .select('*')
          .eq('user_id', user.id)
          .order('date', { ascending: false });

        if (error) throw error;
        setRecords(data || []);

      } catch (err) {
        console.error('Error loading medical records:', err);
        setError('Failed to load medical records');
      } finally {
        setLoading(false);
      }
    };

    loadRecords();
  }, [user]);

  const handleViewRecord = (recordId: string) => {
    console.log('View record:', recordId);
  };

  const handleDownloadRecord = (recordId: string) => {
    console.log('Download record:', recordId);
  };

  const handleShareRecord = (recordId: string) => {
    console.log('Share record:', recordId);
  };

  const handleEditRecord = (recordId: string) => {
    console.log('Edit record:', recordId);
  };

  const handleDeleteRecord = (recordId: string) => {
    console.log('Delete record:', recordId);
  };

  const handleAddRecord = () => {
    console.log('Add new record');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'checkup':
        return 'text-green-600 bg-green-100';
      case 'test':
        return 'text-blue-600 bg-blue-100';
      case 'procedure':
        return 'text-purple-600 bg-purple-100';
      case 'prescription':
        return 'text-orange-600 bg-orange-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const filteredRecords = records.filter(record => {
    const matchesSearch = record.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (record.diagnosis && record.diagnosis.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesFilter = selectedFilter === 'all' || record.type.toLowerCase() === selectedFilter;
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="p-4 pb-safe space-y-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 pb-safe space-y-4">
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <AlertCircle size={16} className="text-red-500" />
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 pb-safe space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Medical Records</h2>
          <p className="text-sm text-gray-600">View and manage your medical history</p>
        </div>
        <Button size="sm" onClick={handleAddRecord}>
          <Plus size={16} className="mr-2" />
          Add
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search medical records..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
        />
      </div>

      {/* Filter */}
      <div className="flex gap-2 overflow-x-auto">
        <Button
          variant={selectedFilter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedFilter('all')}
          className="whitespace-nowrap"
        >
          All
        </Button>
        <Button
          variant={selectedFilter === 'checkup' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedFilter('checkup')}
          className="whitespace-nowrap"
        >
          Checkups
        </Button>
        <Button
          variant={selectedFilter === 'test' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedFilter('test')}
          className="whitespace-nowrap"
        >
          Tests
        </Button>
        <Button
          variant={selectedFilter === 'procedure' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedFilter('procedure')}
          className="whitespace-nowrap"
        >
          Procedures
        </Button>
      </div>

      {/* Records List */}
      <div className="space-y-3">
        {filteredRecords.length === 0 ? (
          <Card className="p-8 text-center">
            <FileText size={48} className="text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Medical Records</h3>
            <p className="text-gray-600 mb-4">Start by adding your first medical record</p>
            <Button onClick={handleAddRecord}>
              <Plus size={16} className="mr-2" />
              Add Record
            </Button>
          </Card>
        ) : (
          filteredRecords.map((record) => (
            <Card key={record.id} className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText size={16} className="text-blue-500" />
                    <h3 className="font-medium text-gray-900">{record.type}</h3>
                  </div>
                  <p className="text-sm text-gray-600">{record.type} • {record.diagnosis || 'No diagnosis'}</p>
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Calendar size={12} />
                    {formatDate(record.date)}
                  </p>
                  {record.treatment && (
                    <p className="text-xs text-gray-500 mt-1">Treatment: {record.treatment}</p>
                  )}
                  {record.notes && (
                    <p className="text-xs text-gray-500 mt-1">{record.notes}</p>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => handleViewRecord(record.id)}
                  >
                    <Eye size={14} />
                  </Button>
                  {record.attachments && record.attachments.length > 0 && (
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => handleDownloadRecord(record.id)}
                    >
                      <Download size={14} />
                    </Button>
                  )}
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => handleShareRecord(record.id)}
                  >
                    <Share2 size={14} />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => handleEditRecord(record.id)}
                  >
                    <Edit size={14} />
                  </Button>
                </div>
              </div>
              
              {/* Medications */}
              {record.medications && record.medications.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-3">
                  {record.medications.map((medication, index) => (
                    <span key={index} className="px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded-full">
                      {medication}
                    </span>
                  ))}
                </div>
              )}

              {/* Type Badge */}
              <div className="mt-3">
                <span className={`px-2 py-1 rounded-full text-xs ${getTypeColor(record.type)}`}>
                  {record.type}
                </span>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
} 